import { Theme } from '../types';
import {
  getEntries,
  groupReplacements,
  typographyReplacements,
} from './createCssVars';
import { safeLocalStorageGet } from './safeLocalStorage';

type StringRecord = Record<string, string>;
type TypographyRecord = Record<string, Record<string, string>>;

type CssVarsTheme = {
  color: StringRecord;
  space: StringRecord;
  borderRadius: StringRecord;
  borderWidth: StringRecord;
  fontSize?: StringRecord;
  fontWeight?: StringRecord;
  boxShadow: StringRecord;
  typography: TypographyRecord;
};

export const LOCAL_STORAGE_DANGEROUSLY_USE_CSS_VARIABLES_KEY =
  'trellis:local-dev-only-use-css-variables';

export function getUseCssVariables() {
  const useCssVariables = safeLocalStorageGet(
    LOCAL_STORAGE_DANGEROUSLY_USE_CSS_VARIABLES_KEY
  );
  return useCssVariables === 'true';
}

/**
 * Returns a version of baseTheme that uses CSS variables for all values.
 * This is only intended for use in local development.
 * @param baseTheme
 * @returns
 */
export const getCssVariablesTheme = (baseTheme: Theme) => {
  const cssVarsTheme: CssVarsTheme = {
    color: {},
    space: {},
    borderRadius: {},
    borderWidth: {},
    boxShadow: {},
    typography: {},
  };

  for (const [group, values] of getEntries(baseTheme)) {
    if (group === 'typography') {
      const currentTypographyGroup = cssVarsTheme.typography!;

      for (const [varName, typographyValues] of getEntries(values)) {
        const currentStyleObject = (currentTypographyGroup[varName] ??= {});

        for (const [key, value] of getEntries(typographyValues)) {
          if (value == null) {
            continue;
          }
          const replacementKey =
            typographyReplacements[key as keyof typeof typographyReplacements];
          const cssVariableName = `var(--trellis-${group}-${varName}-${replacementKey}, ${value})`;
          currentStyleObject[key] = cssVariableName;
        }
      }
    } else {
      const groupName =
        groupReplacements[group as keyof typeof groupReplacements] || group;
      const currentGroup = (cssVarsTheme[
        group as Exclude<keyof CssVarsTheme, 'typography'>
      ] ??= {});

      for (const [varName, value] of Object.entries(values)) {
        if (value == null) {
          continue;
        }
        const cssVariableName = `var(--trellis-${groupName}-${varName}, ${value})`;
        currentGroup[varName] = cssVariableName;
      }
    }
  }

  return {
    ...cssVarsTheme,
    theme: baseTheme,
  } as unknown as Theme;
};
